/* Native swipe from mt-inspiration; only visible clips load, two play at most. */
(() => {
  if (customElements.get('mt-collection-looks')) return;
  class MTCollectionLooks extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const controller = this.controller;
      customElements.whenDefined('mt-inspiration').then(() => {
        if (!this.isConnected || controller.signal.aborted) return;
        this.initialize();
      });
    }

    initialize() {
      this.gallery = this.querySelector('mt-inspiration');
      this.viewport = this.querySelector('.mt-inspiration__viewport');
      this.track = this.querySelector('.mt-inspiration__track');
      this.dialog = this.querySelector('dialog');
      this.player = this.dialog?.querySelector('video');
      this.error = this.dialog?.querySelector('[data-video-error]');
      if (!this.track || !this.dialog) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.videos = new Map();
      this.visible = false;
      const on = (el, name, fn) => el.addEventListener(name, fn, { signal: this.controller.signal });
      on(this, 'click', event => {
        const card = event.target.closest('[data-look-video]');
        if (!card || !this.contains(card) || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !this.dialog.showModal) return;
        event.preventDefault();
        this.openVideo(card);
      });
      on(this.dialog.querySelector('[data-close-video]'), 'click', () => this.dialog.close());
      on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      });
      on(this.dialog, 'close', () => this.releasePlayer(true));
      on(this.player, 'error', () => { this.error.hidden = false; });
      on(document, 'visibilitychange', () => {
        if (document.hidden) this.player.pause();
        this.sync();
      });
      on(this.motion, 'change', () => this.sync());
      if (navigator.connection?.addEventListener) on(navigator.connection, 'change', () => this.sync());
      if ('IntersectionObserver' in window) {
        this.cardObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (this.videos.has(entry.target)) this.videos.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0); });
          this.sync();
        }, { root: this.viewport, threshold: [0, .15, .5, .9] });
        this.outerObserver = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.sync();
        });
        this.outerObserver.observe(this);
      }
      this.scan();
      this.mutationObserver = new MutationObserver(() => this.scan());
      this.mutationObserver.observe(this.track, { childList: true });
    }

    scan() {
      for (const video of this.videos.keys()) {
        if (!this.track.contains(video)) {
          video.pause();
          video.removeAttribute('src');
          video.load();
          this.cardObserver?.unobserve(video);
          this.videos.delete(video);
        }
      }
      this.track.querySelectorAll('video[data-src]').forEach(video => {
        if (this.videos.has(video)) return;
        // A ribbon rebuild can copy an already loaded source into a new clone.
        if (video.closest('[data-mt-clone]') && video.hasAttribute('src')) {
          video.removeAttribute('src');
          video.load();
        }
        this.videos.set(video, 0);
        this.cardObserver?.observe(video);
      });
      this.sync();
    }

    sync() {
      if (!this.videos) return;
      const active = this.visible && !document.hidden && !this.dialog.open;
      const visible = active ? [...this.videos].filter(([, ratio]) => ratio > .15).sort((a, b) => b[1] - a[1]).map(([video]) => video) : [];
      const playing = !this.motion.matches && !navigator.connection?.saveData ? visible.slice(0, 2) : [];
      for (const video of visible) {
        if (!video.hasAttribute('src')) {
          video.muted = true;
          video.preload = 'metadata';
          video.src = video.dataset.src + '#t=0.001';
          video.load();
        }
      }
      for (const video of this.videos.keys()) {
        if (playing.includes(video)) {
          if (video.paused) video.play()?.catch(() => {});
        } else video.pause();
      }
    }

    openVideo(card) {
      this.trigger = card;
      this.gallery.takeControl?.();
      this.dialog.querySelector('[data-video-title]').textContent = card.dataset.caption;
      const link = this.dialog.querySelector('[data-video-product]');
      link.href = card.href;
      link.textContent = card.dataset.productLabel;
      this.error.hidden = true;
      this.dialog.showModal();
      this.scrollStyle = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.player.muted = true;
      this.player.src = card.dataset.lookVideo;
      this.player.load();
      this.player.play()?.catch(() => {});
      this.sync();
      this.dialog.querySelector('[data-close-video]').focus({ preventScroll: true });
    }

    releasePlayer(restoreFocus) {
      if (!this.player) return;
      this.player.pause();
      this.player.removeAttribute('src');
      this.player.load();
      if (this.scrollStyle !== undefined) {
        if (document.documentElement.style.overflow === 'hidden') document.documentElement.style.overflow = this.scrollStyle;
        this.scrollStyle = undefined;
      }
      if (restoreFocus && this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
      this.trigger = null;
      this.sync();
    }

    disconnectedCallback() {
      this.visible = false;
      this.controller?.abort();
      this.outerObserver?.disconnect();
      this.cardObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.releasePlayer(false);
      if (this.dialog?.open) this.dialog.close();
      this.videos?.forEach((ratio, video) => { video.pause(); video.removeAttribute('src'); video.load(); });
      this.videos?.clear();
      this.controller = null;
    }
  }
  customElements.define('mt-collection-looks', MTCollectionLooks);
})();
